/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { getHygraphCosmosPageData, getOwnerConfigFromEnv, toHygraphSlug } from "@cosmos/hygraph-data";
import { Locale, PageHeroAndSectionsMetadataFragment } from "@cosmos/hygraph-schema";
import { getHygraphLocale, getSlugParam } from "@cosmos/next-common";
import { isError } from "@epic-mw/error-types";
import { Metadata } from "next";
import { notFound } from "next/navigation";

import { CosmosPageMetadata } from "../common/page.types";
import { checkValidParams } from "../templates/selector";

const getPageMetadata = async (
	locale: string = Locale.En,
	slug: string,
	previewParam?: string | string[],
): Promise<PageHeroAndSectionsMetadataFragment | null | undefined> => {
	if (!slug) {
		throw new Error("Missing slug");
	}

	const preview = previewParam && typeof previewParam === "string" ? previewParam : undefined;

	// Get owner config from environment to determine slug prefix
	const ownerConfig = await getOwnerConfigFromEnv(preview);

	// Warn if owner ID is set but not found in Hygraph (misconfiguration)
	if (ownerConfig.ownerId && !ownerConfig.pageSlugPrefix && !ownerConfig.isDefaultOwner) {
		console.warn(
			`Owner "${ownerConfig.ownerId}" not found in Hygraph or missing pageSlugPrefix - content may not load correctly`,
		);
	}

	// Transform URL path to Hygraph slug by prepending prefix
	const hygraphSlug = toHygraphSlug(slug, ownerConfig.pageSlugPrefix);

	// Build locale array for query
	const localeArray = [locale, Locale.En] as Locale[];

	/**
	 * Fetch the page metadata using the transformed Hygraph slug with prefix.
	 */
	const resp = await getHygraphCosmosPageData(
		"CosmosPageHeroAndSectionMetadata",
		{
			locale: localeArray,
			slug: hygraphSlug,
		},
		preview,
	);
	return resp?.cosmosPageHeroAndSection;
};

export async function cosmosPageHeroAndSectionMetadata({
	slug: rootSlug = "",
	params,
	searchParams,
	hygraphLocales,
	getAppMetadata,
}: CosmosPageMetadata): Promise<Metadata> {
	const valid = checkValidParams({ params, searchParams });
	if (!valid) {
		return notFound();
	}

	const slugParam = getSlugParam(params);
	const slug = `${rootSlug}${slugParam}`;
	const locale = params.lang;
	let pageData: PageHeroAndSectionsMetadataFragment | null | undefined;
	try {
		pageData = await getPageMetadata(getHygraphLocale(hygraphLocales, locale), slug, searchParams.preview);
	} catch (e) {
		console.error("Failed to get cosmosPageHeroAndSectionMetadata()", { error: isError(e) && e.message });
	}
	const metaTags = pageData?.metaTags ?? {};

	// Pass the original URL slug (without prefix) for canonical URL generation
	return getAppMetadata(metaTags, slug, locale);
}
